import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import SEOHead from '../../components/SEOHead';

export default function FAQPage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const faqs = [
    {
      category: "Getting Started",
      questions: [
        {
          q: "How does Technelle approach creative and strategy development?",
          a: "We follow a structured multi-phase process tailored to Wisconsin businesses. First, we conduct discovery through interviews, workshops, and audience analysis. Next, we create a strategic framework that informs content, design, and channel decisions. Execution follows with SEO-driven content, campaign builds, and creative production. Throughout, we maintain close communication and iterate based on data and feedback from our Wisconsin clients."
        },
        {
          q: "What makes Technelle different from other marketing agencies?",
          a: "Three key differentiators: 1) We combine 20+ years of hands-on marketing expertise with cutting-edge AI implementation—not just theory. 2) We're selective about clients and focused on measurable ROI, not vanity metrics. 3) Our Waukesha-based team brings real-world experience in specialized areas like tradeshow management, ABM campaigns, and workflow automation that most agencies don't offer. We understand the unique challenges facing Wisconsin businesses."
        },
        {
          q: "Do you work with businesses outside of Wisconsin?",
          a: "Absolutely. While we're based in Waukesha and serve many Wisconsin businesses throughout Milwaukee, Madison, Green Bay, and across the state, we work with clients nationwide. All our services are delivered remotely with video calls, screen sharing, and collaborative tools. Location doesn't limit our ability to deliver exceptional results."
        }
      ]
    },
    {
      category: "Services & Pricing",
      questions: [
        {
          q: "How long does a typical project take?",
          a: "Timelines vary by deliverable. Standard design and content projects are completed within days or weeks, depending on project scope. Larger engagements, such as full brand development or multi-platform campaigns, follow multi-month timelines with defined milestones. AI implementation projects for Wisconsin businesses typically range from 2-6 months depending on complexity. We'll provide a detailed timeline during your strategy call."
        },
        {
          q: "What kind of ROI can I expect?",
          a: "ROI varies by scope, but our engagements focus on measurable outcomes such as increased organic visibility, improved conversion rates, higher lead volume, and stronger audience retention. We define KPIs at the outset and track performance throughout. Typical Wisconsin clients see positive ROI within 3-6 months, with results compounding over time. We provide detailed analytics and reporting so you always know where you stand."
        },
        {
          q: "What's your pricing model?",
          a: "We offer three engagement models: 1) Project-Based: Fixed scope and price for specific initiatives (website redesign, AI tool development, etc.). 2) Retainer: Monthly ongoing partnership for continuous marketing and optimization (6-month minimum). 3) Advisory: Strategic consultation for Wisconsin teams executing internally. Pricing is customized based on your needs, goals, and scope. We'll provide a detailed proposal after your free strategy call."
        },
        {
          q: "Do you require long-term contracts?",
          a: "For retainer engagements, we require a 6-month minimum commitment. This allows enough time to implement strategies, gather data, and optimize for results. Marketing transformation doesn't happen overnight—we're focused on sustainable growth for Wisconsin businesses, not quick fixes. Project-based work is completed according to the agreed timeline with no ongoing commitment required."
        }
      ]
    },
    {
      category: "AI Implementation",
      questions: [
        {
          q: "What does 'practical AI' actually mean?",
          a: "Practical AI means business-ready solutions that deliver measurable value from day one—not experimental technology or AI for AI's sake. We implement proven tools like chatbots, workflow automation, predictive analytics, and personalization engines that solve real problems for Wisconsin businesses: reduce costs, save time, or increase revenue. No hype, no jargon, just results you can measure."
        },
        {
          q: "Do I need technical knowledge to implement AI?",
          a: "Not at all. We handle everything from strategy to implementation to training for Wisconsin businesses. You don't need technical expertise—just a willingness to embrace new processes. We design AI solutions that fit seamlessly into your existing workflows and train your team on how to use them effectively. Our goal is to make AI accessible and valuable for non-technical business owners."
        },
        {
          q: "How do you ensure AI implementation is ethical and secure?",
          a: "We prioritize data privacy, transparency, and ethical AI practices. All implementations comply with relevant regulations (GDPR, CCPA, etc.). We conduct thorough security audits, implement proper access controls, and ensure customer data is protected. We also design AI systems to be explainable and bias-free, so you can trust the decisions and recommendations they make."
        },
        {
          q: "What AI tools do you typically implement?",
          a: "Common implementations for Wisconsin businesses include: AI chatbots for 24/7 customer service, automated email and SMS workflows, predictive lead scoring systems, dynamic pricing engines, content personalization tools, and intelligent analytics dashboards. We also build custom AI solutions tailored to your specific business needs using platforms like OpenAI, Google AI, and enterprise automation tools."
        }
      ]
    },
    {
      category: "Industries & Specialization",
      questions: [
        {
          q: "What industries do you specialize in?",
          a: "We work across Hospitality, Food & Beverage, Industrial, Utilities, Manufacturing, Renewable Energy, Energy Storage, Trade Services, Health & Beauty, and Ecommerce throughout Wisconsin. Our clients choose us for strategic storytelling, digital performance, and creative that adapts to their market. We also bring specialized experience in tradeshow management, corporate event planning, ABM campaigns, and wedding coordination."
        },
        {
          q: "Do you only work with certain business sizes?",
          a: "We specialize in small and mid-size Wisconsin businesses—typically companies with $1M-$50M in annual revenue. These businesses have outgrown DIY marketing but aren't ready for enterprise-level agency fees. We're the right fit if you're serious about growth, willing to invest in quality, and ready to implement data-driven strategies."
        },
        {
          q: "Can you help with B2B and B2C marketing?",
          a: "Yes, we work with both B2B and B2C companies throughout Wisconsin. Our approach adapts to your audience and sales cycle. For B2B, we focus on lead generation, account-based marketing, and long-term nurture campaigns. For B2C, we emphasize conversion optimization, customer retention, and lifecycle marketing. The fundamentals of strategic marketing and AI implementation apply to both."
        }
      ]
    },
    {
      category: "Process & Collaboration",
      questions: [
        {
          q: "How involved do I need to be in the process?",
          a: "We're partners, not vendors—so some involvement is required. During discovery and strategy phases, expect weekly meetings and collaboration on key decisions. During execution, we handle the heavy lifting but need your input on brand voice, messaging, and approvals. Plan for 3-5 hours per week during active projects. We respect your time and keep meetings focused and productive."
        },
        {
          q: "What tools do you use for collaboration?",
          a: "We adapt to your preferred platforms but typically use: Zoom or Google Meet for video calls, Slack or email for communication, Asana or Monday for project management, Google Drive or Dropbox for file sharing, and Figma or Canva for design collaboration. We'll integrate with your existing tools whenever possible to minimize friction."
        },
        {
          q: "How do you measure and report results?",
          a: "We establish clear KPIs at project kickoff based on your Wisconsin business goals. Reporting frequency depends on engagement type—typically monthly for retainers, at milestones for projects. Reports include performance data, insights, and recommendations for optimization. We use dashboards, Google Analytics, CRM data, and campaign analytics to track progress. You'll always know what's working and what needs adjustment."
        },
        {
          q: "What happens if I'm not satisfied with the results?",
          a: "We define success metrics upfront and provide regular updates. If results aren't meeting expectations, we dig into the data, identify issues, and adjust strategy. For retainer clients, we build in optimization cycles specifically for this purpose. If we're truly not the right fit, we'll have an honest conversation and help you transition. Our reputation is built on results, so we're invested in your success."
        }
      ]
    }
  ];

  // Build FAQPage structured data
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.flatMap(section => 
      section.questions.map(faq => ({
        "@type": "Question",
        "name": faq.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.a
        }
      }))
    )
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <SEOHead 
        title="FAQ | Wisconsin AI Marketing & Consulting Questions | Technelle"
        description="Frequently asked questions about AI marketing and consulting services for Wisconsin businesses. Get answers about our process, pricing, timelines, and how we help Waukesha and Wisconsin companies grow through AI-powered marketing strategies."
        keywords="AI marketing FAQ, Wisconsin marketing questions, AI consulting FAQ, marketing agency questions Wisconsin, Waukesha marketing consultant FAQ, AI implementation questions"
        canonical="https://technelle.ai/faq"
        ogTitle="FAQ | Wisconsin AI Marketing & Consulting Questions | Technelle"
        ogDescription="Common questions about AI marketing and consulting services for Wisconsin businesses."
        ogUrl="https://technelle.ai/faq"
        structuredData={faqStructuredData}
        breadcrumbs={[
          { name: 'Home', url: 'https://technelle.ai/' },
          { name: 'FAQ', url: 'https://technelle.ai/faq' }
        ]}
      />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="container mx-auto px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/d2c67bee-2653-4219-a221-2e9d779e273e_ChatGPT-Image-Apr-2-2026-02_21_57-PM.png?v=d5a360501a5e3b86884e2dc0f7061a82" alt="Technelle - AI Marketing & Consulting" className="h-14" />
          </Link>
          <div className="hidden lg:flex items-center gap-10">
            <Link to="/" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">Home</Link>
            <Link to="/about" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">About</Link>
            <Link to="/services" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">Services</Link>
            <Link to="/resources" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">Resources</Link>
            <Link to="/faq" className="text-[15px] text-[#2196F3] transition-colors font-medium">FAQ</Link>
          </div>
          <button 
            onClick={() => setMenuOpen(true)}
            className="bg-[#2196F3] text-white px-6 sm:px-8 py-3 rounded-md text-[15px] font-medium hover:bg-[#1976D2] transition-colors whitespace-nowrap cursor-pointer min-w-[44px] min-h-[44px]"
            aria-label="Book free strategy call"
          >
            Book Free Strategy Call
          </button>
        </div>
      </nav>

      {/* Contact Overlay */}
      {menuOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-start justify-end">
          <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl">
            <div className="p-8 sm:p-12">
              <div className="flex items-center justify-between mb-12">
                <h2 className="text-[28px] sm:text-[32px] font-bold text-[#1a1a1a]">Let's talk —</h2>
                <button 
                  onClick={() => setMenuOpen(false)}
                  className="text-[#1a1a1a] hover:text-[#2196F3] transition-colors cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Close contact form"
                >
                  <span className="text-[15px] font-medium">Close</span>
                </button>
              </div>
              <p className="text-[15px] text-gray-600">Please reach out to schedule your free strategy call at <a href="tel:4145309114" className="text-[#2196F3] hover:underline">414-530-9114</a> or <a href="mailto:melissa@technelle.ai" className="text-[#2196F3] hover:underline">melissa@technelle.ai</a></p>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative bg-gray-50 pt-32 pb-16">
        <div className="container mx-auto px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-[56px] sm:text-[68px] leading-[1.1] font-bold text-[#1a1a1a] mb-6 font-serif">
              Frequently Asked <span className="relative inline-block text-[#2196F3]">
                Questions
                <svg className="absolute -bottom-1 left-0 w-full" viewBox="0 0 300 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M2 6C100 2 200 2 298 6" stroke="#2196F3" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </span>
            </h1>
            <p className="text-[18px] text-gray-600 leading-relaxed">
              Everything you need to know about working with Technelle, our services, and how we help Wisconsin businesses grow.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="bg-white py-24">
        <div className="container mx-auto px-8">
          <div className="max-w-4xl mx-auto space-y-16">
            {faqs.map((section, sectionIdx) => (
              <div key={sectionIdx}>
                <h2 className="text-[32px] font-serif font-bold text-[#1a1a1a] mb-8 pb-4 border-b-2 border-[#2196F3]">
                  {section.category}
                </h2>
                <div className="space-y-4">
                  {section.questions.map((faq, faqIdx) => {
                    const globalIndex = faqs.slice(0, sectionIdx).reduce((acc, s) => acc + s.questions.length, 0) + faqIdx;
                    const isOpen = openIndex === globalIndex;
                    
                    return (
                      <div key={faqIdx} className="bg-gray-50 rounded-xl overflow-hidden">
                        <button
                          onClick={() => setOpenIndex(isOpen ? null : globalIndex)}
                          className="w-full flex items-center justify-between p-6 text-left cursor-pointer hover:bg-gray-100 transition-colors"
                          aria-expanded={isOpen}
                        >
                          <span className="text-[17px] font-semibold text-[#1a1a1a] pr-4">
                            {faq.q}
                          </span>
                          <i className={`ri-arrow-down-s-line text-2xl text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true"></i>
                        </button>
                        {isOpen && (
                          <div className="px-6 pb-6">
                            <p className="text-[15px] text-gray-700 leading-relaxed">{faq.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="bg-gray-50 py-24">
        <div className="container mx-auto px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-[48px] font-serif font-bold text-[#1a1a1a] mb-6">
              Still Have <span className="relative inline-block text-[#2196F3]">
                Questions
                <svg className="absolute -bottom-1 left-0 w-full" viewBox="0 0 220 6" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M2 4C60 2 160 2 218 4" stroke="#2196F3" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </span>?
            </h2>
            <p className="text-[18px] text-gray-600 mb-10 leading-relaxed">
              Book a free 30-minute strategy call. We'll answer your questions and explore how we can help your Wisconsin business grow.
            </p>
            <button 
              onClick={() => setMenuOpen(true)}
              className="bg-[#2196F3] text-white px-10 py-4 rounded-md text-[15px] font-medium hover:bg-[#1976D2] transition-colors cursor-pointer min-w-[44px] min-h-[44px]"
            >
              <span>Book Your Free Strategy Call</span>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a1a1a] text-white py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            <div className="lg:col-span-1">
              <div className="flex items-center gap-3 mb-6">
                <img src="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/d2c67bee-2653-4219-a221-2e9d779e273e_ChatGPT-Image-Apr-2-2026-02_21_57-PM.png?v=d5a360501a5e3b86884e2dc0f7061a82" alt="Technelle" className="h-12" width="48" height="48" />
              </div>
            </div>

            <div>
              <h5 className="text-[14px] uppercase tracking-wider text-gray-400 mb-6 font-semibold">Links</h5>
              <nav className="space-y-3">
                <Link to="/" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Home</Link>
                <Link to="/about" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">About</Link>
                <Link to="/services" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Services</Link>
                <Link to="/faq" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">FAQ</Link>
                <Link to="/resources" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Resources</Link>
              </nav>
            </div>

            <div>
              <h5 className="text-[14px] uppercase tracking-wider text-gray-400 mb-6 font-semibold">Contact</h5>
              <div className="space-y-3">
                <a href="tel:4145309114" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">414-530-9114</a>
                <a href="mailto:melissa@technelle.ai" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">melissa@technelle.ai</a>
              </div>
            </div>

            <div>
              <h5 className="text-[14px] uppercase tracking-wider text-gray-400 mb-6 font-semibold">Legal</h5>
              <nav className="space-y-3">
                <Link to="/privacy-policy" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Privacy Policy</Link>
                <Link to="/terms" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Terms</Link>
              </nav>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              <div className="text-[14px] text-gray-400">© 2025 Technelle. All rights reserved.</div>

            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
